export interface SocialMediaProfile {
  platform: string;
  url: string;
  username?: string;
  followers?: number;
  engagement?: number;
  verified?: boolean;
  completeness?: number;
  lastPost?: string;
}

export interface WebsiteQuality {
  contentRichness: number;
  hasRealContent: boolean;
  wordCount: number;
  hasImages: boolean;
  hasNavigation: boolean;
  hasContactInfo: boolean;
  isBlankWebsite: boolean;
  score: number;
}

export interface SocialMediaData {
  platforms: SocialMediaProfile[];
  score: number;
  detectionMethods: string[];
  businessName?: string;
  domain?: string;
  websiteQuality?: WebsiteQuality;
}

export class SocialMediaDetector {
  private readonly socialPlatforms = [
    // 🧠 General Social Networks
    { name: 'facebook', domains: ['facebook.com', 'fb.com'], patterns: [/(?:https?:\/\/)?(?:www\.|m\.)?(?:facebook|fb)\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 25 },
    { name: 'instagram', domains: ['instagram.com'], patterns: [/(?:https?:\/\/)?(?:www\.|m\.)?instagram\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 20 },
    { name: 'twitter', domains: ['twitter.com', 'x.com'], patterns: [/(?:https?:\/\/)?(?:www\.|mobile\.)?(?:twitter|x)\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 20 },
    { name: 'tiktok', domains: ['tiktok.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@([a-zA-Z0-9._-]+)/gi], baseScore: 18 },
    { name: 'snapchat', domains: ['snapchat.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?snapchat\.com\/add\/([a-zA-Z0-9._-]+)/gi], baseScore: 12 },
    { name: 'pinterest', domains: ['pinterest.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?pinterest\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 15 },
    { name: 'linkedin', domains: ['linkedin.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/(?:company|in)\/([a-zA-Z0-9._-]+)/gi], baseScore: 15 },
    { name: 'reddit', domains: ['reddit.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?reddit\.com\/(?:r|user)\/([a-zA-Z0-9._-]+)/gi], baseScore: 12 },
    { name: 'threads', domains: ['threads.net'], patterns: [/(?:https?:\/\/)?(?:www\.)?threads\.net\/@?([a-zA-Z0-9._-]+)/gi], baseScore: 15 },
    { name: 'tumblr', domains: ['tumblr.com'], patterns: [/(?:https?:\/\/)?([a-zA-Z0-9._-]+)\.tumblr\.com/gi], baseScore: 10 },

    // 💬 Messaging & Communication Platforms
    { name: 'whatsapp', domains: ['wa.me', 'whatsapp.com'], patterns: [/(?:https?:\/\/)?(?:wa\.me|api\.whatsapp\.com)\/([0-9]+)/gi], baseScore: 8 },
    { name: 'telegram', domains: ['t.me', 'telegram.me'], patterns: [/(?:https?:\/\/)?(?:t\.me|telegram\.me)\/([a-zA-Z0-9._-]+)/gi], baseScore: 10 },
    { name: 'discord', domains: ['discord.gg', 'discord.com'], patterns: [/(?:https?:\/\/)?(?:discord\.gg|discord\.com\/invite)\/([a-zA-Z0-9]+)/gi], baseScore: 12 },
    { name: 'signal', domains: ['signal.group'], patterns: [/(?:https?:\/\/)?signal\.group\/([a-zA-Z0-9_-]+)/gi], baseScore: 7 },
    { name: 'viber', domains: ['viber.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?viber\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 6 },
    { name: 'wechat', domains: ['weixin.qq.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?weixin\.qq\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 8 },
    { name: 'line', domains: ['line.me'], patterns: [/(?:https?:\/\/)?line\.me\/([a-zA-Z0-9._-]+)/gi], baseScore: 7 },
    { name: 'skype', domains: ['skype.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?skype\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 6 },

    // 🎥 Video & Streaming Platforms
    { name: 'youtube', domains: ['youtube.com', 'youtu.be'], patterns: [/(?:https?:\/\/)?(?:www\.)?youtube\.com\/(?:c\/|channel\/|user\/|@)([a-zA-Z0-9._-]+)/gi], baseScore: 15 },
    { name: 'twitch', domains: ['twitch.tv'], patterns: [/(?:https?:\/\/)?(?:www\.)?twitch\.tv\/([a-zA-Z0-9._-]+)/gi], baseScore: 14 },
    { name: 'vimeo', domains: ['vimeo.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 10 },
    { name: 'dailymotion', domains: ['dailymotion.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 8 },
    { name: 'rumble', domains: ['rumble.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?rumble\.com\/(?:c|user)\/([a-zA-Z0-9._-]+)/gi], baseScore: 9 },
    { name: 'bilibili', domains: ['bilibili.com', 'space.bilibili.com'], patterns: [/(?:https?:\/\/)?space\.bilibili\.com\/([0-9]+)/gi], baseScore: 8 },
    { name: 'trovo', domains: ['trovo.live'], patterns: [/(?:https?:\/\/)?(?:www\.)?trovo\.live\/([a-zA-Z0-9._-]+)/gi], baseScore: 7 },

    // 🎶 Music & Audio Sharing Platforms
    { name: 'soundcloud', domains: ['soundcloud.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?soundcloud\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 12 },
    { name: 'spotify', domains: ['spotify.com'], patterns: [/(?:https?:\/\/)?open\.spotify\.com\/(?:user|artist)\/([a-zA-Z0-9]+)/gi], baseScore: 12 },
    { name: 'audiomack', domains: ['audiomack.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?audiomack\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 8 },
    { name: 'mixcloud', domains: ['mixcloud.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?mixcloud\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 9 },
    { name: 'bandcamp', domains: ['bandcamp.com'], patterns: [/(?:https?:\/\/)?([a-zA-Z0-9._-]+)\.bandcamp\.com/gi], baseScore: 10 },
    { name: 'reverbnation', domains: ['reverbnation.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?reverbnation\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 7 },
    { name: 'lastfm', domains: ['last.fm'], patterns: [/(?:https?:\/\/)?(?:www\.)?last\.fm\/user\/([a-zA-Z0-9._-]+)/gi], baseScore: 8 },

    // 🧑‍💻 Developer & Tech Communities
    { name: 'github', domains: ['github.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?github\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 14 },
    { name: 'gitlab', domains: ['gitlab.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?gitlab\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 12 },
    { name: 'stackoverflow', domains: ['stackoverflow.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?stackoverflow\.com\/users\/([0-9]+)/gi], baseScore: 10 },
    { name: 'devto', domains: ['dev.to'], patterns: [/(?:https?:\/\/)?dev\.to\/([a-zA-Z0-9._-]+)/gi], baseScore: 11 },
    { name: 'hashnode', domains: ['hashnode.com'], patterns: [/(?:https?:\/\/)?([a-zA-Z0-9._-]+)\.hashnode\.dev/gi], baseScore: 9 },
    { name: 'codepen', domains: ['codepen.io'], patterns: [/(?:https?:\/\/)?codepen\.io\/([a-zA-Z0-9._-]+)/gi], baseScore: 10 },
    { name: 'behance', domains: ['behance.net'], patterns: [/(?:https?:\/\/)?(?:www\.)?behance\.net\/([a-zA-Z0-9._-]+)/gi], baseScore: 11 },
    { name: 'dribbble', domains: ['dribbble.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?dribbble\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 11 },
    { name: 'producthunt', domains: ['producthunt.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?producthunt\.com\/@([a-zA-Z0-9._-]+)/gi], baseScore: 9 },
    { name: 'indiehackers', domains: ['indiehackers.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?indiehackers\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 8 },

    // 📷 Photography & Design Networks
    { name: 'flickr', domains: ['flickr.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?flickr\.com\/(?:people|photos)\/([a-zA-Z0-9@._-]+)/gi], baseScore: 9 },
    { name: '500px', domains: ['500px.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?500px\.com\/(?:p\/)?([a-zA-Z0-9._-]+)/gi], baseScore: 10 },
    { name: 'deviantart', domains: ['deviantart.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9._-]+)\.deviantart\.com/gi], baseScore: 9 },
    { name: 'artstation', domains: ['artstation.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?artstation\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 10 },
    { name: 'unsplash', domains: ['unsplash.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?unsplash\.com\/@([a-zA-Z0-9._-]+)/gi], baseScore: 9 },
    { name: 'vsco', domains: ['vsco.co'], patterns: [/(?:https?:\/\/)?(?:www\.)?vsco\.co\/([a-zA-Z0-9._-]+)/gi], baseScore: 8 },

    // 📰 Blogging & Writing Platforms
    { name: 'medium', domains: ['medium.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?medium\.com\/@([a-zA-Z0-9._-]+)/gi], baseScore: 13 },
    { name: 'substack', domains: ['substack.com'], patterns: [/(?:https?:\/\/)?([a-zA-Z0-9._-]+)\.substack\.com/gi], baseScore: 12 },
    { name: 'wordpress', domains: ['wordpress.com'], patterns: [/(?:https?:\/\/)?([a-zA-Z0-9._-]+)\.wordpress\.com/gi], baseScore: 10 },
    { name: 'blogger', domains: ['blogger.com', 'blogspot.com'], patterns: [/(?:https?:\/\/)?([a-zA-Z0-9._-]+)\.blogspot\.com/gi], baseScore: 8 },
    { name: 'ghost', domains: ['ghost.io'], patterns: [/(?:https?:\/\/)?([a-zA-Z0-9._-]+)\.ghost\.io/gi], baseScore: 9 },
    { name: 'wattpad', domains: ['wattpad.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?wattpad\.com\/user\/([a-zA-Z0-9._-]+)/gi], baseScore: 7 },
    { name: 'quora', domains: ['quora.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?quora\.com\/profile\/([a-zA-Z0-9._-]+)/gi], baseScore: 10 },

    // 🧩 Community & Forums
    { name: 'discourse', domains: ['discourse.org'], patterns: [/(?:https?:\/\/)?([a-zA-Z0-9._-]+)\.discourse\.org/gi], baseScore: 7 },
    { name: 'slack', domains: ['slack.com'], patterns: [/(?:https?:\/\/)?([a-zA-Z0-9._-]+)\.slack\.com/gi], baseScore: 8 },
    { name: 'patreon', domains: ['patreon.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?patreon\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 11 },
    { name: 'kofi', domains: ['ko-fi.com'], patterns: [/(?:https?:\/\/)?ko-fi\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 9 },
    { name: 'buymeacoffee', domains: ['buymeacoffee.com'], patterns: [/(?:https?:\/\/)?(?:www\.)?buymeacoffee\.com\/([a-zA-Z0-9._-]+)/gi], baseScore: 9 }
  ];

  async detectSocialMedia(websiteUrl: string, businessName?: string): Promise<SocialMediaData> {
    const detectionMethods: string[] = [];
    const foundProfiles: SocialMediaProfile[] = [];

    try {
      // ONLY Method: Website Crawling - find social links ACTUALLY on the website
      const crawledProfiles = await this.crawlWebsiteForSocialLinks(websiteUrl);
      if (crawledProfiles.length > 0) {
        foundProfiles.push(...crawledProfiles);
        detectionMethods.push('Website Social Links');
      } else {
        console.log('No social media links found on website');
      }

      // NO FALLBACKS - Only show what's actually on the website!

      // Remove duplicates before enhancement
      const deduplicatedProfiles = this.removeDuplicates(foundProfiles);

      // Method 2: Enhanced social profiles from discovered links
      if (deduplicatedProfiles.length > 0) {
        const enhancedFromWebsite = await this.enhanceFoundSocialProfiles(deduplicatedProfiles);
        foundProfiles.splice(0, foundProfiles.length, ...enhancedFromWebsite);
        detectionMethods.push('Social Profile Analysis');
      } else {
        foundProfiles.splice(0, foundProfiles.length);
      }

      // Remove duplicates again after enhancement (in case enhancement created duplicates)
      const uniqueProfiles = this.removeDuplicates(foundProfiles);
      const enhancedProfiles = await this.enhanceProfiles(uniqueProfiles);
      const totalScore = this.calculateOverallScore(enhancedProfiles, undefined);

      return {
        platforms: enhancedProfiles,
        score: totalScore,
        detectionMethods,
        businessName,
        domain: this.extractDomain(websiteUrl)
      };

    } catch (error) {
      console.error('Social media detection failed:', error);
      return {
        platforms: [],
        score: 0,
        detectionMethods: ['Error occurred during detection'],
        businessName,
        domain: this.extractDomain(websiteUrl)
      };
    }
  }

  private async crawlWebsiteForSocialLinks(websiteUrl: string): Promise<SocialMediaProfile[]> {
    try {
      console.log(`🌐 Starting to crawl website: ${websiteUrl}`);
      let htmlContent = '';
      let fetchMethod = '';

      // STEP 1: Try direct fetch first (fastest, works for static sites)
      try {
        console.log('📡 Attempting direct fetch...');
        const directResponse = await fetch(websiteUrl, {
          mode: 'cors',
          headers: {
            'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
          },
          signal: AbortSignal.timeout(10000)
        });

        if (directResponse.ok) {
          htmlContent = await directResponse.text();
          fetchMethod = 'Direct fetch';
          console.log(`✅ Direct fetch succeeded! HTML length: ${htmlContent.length}`);
        } else {
          console.warn(`⚠️ Direct fetch failed with status: ${directResponse.status}`);
        }
      } catch (directError: any) {
        console.warn('⚠️ Direct fetch failed (expected for CORS-restricted sites):', directError.message);
      }

      // STEP 2: Try public CORS proxies (works for most sites)
      if (!htmlContent) {
        console.log('🔄 Trying public CORS proxy services...');
        const proxies = [
          { name: 'AllOrigins', url: `https://api.allorigins.win/raw?url=${encodeURIComponent(websiteUrl)}` },
          { name: 'ThingProxy', url: `https://thingproxy.freeboard.io/fetch/${encodeURIComponent(websiteUrl)}` },
          { name: 'CORS.SH', url: `https://cors.sh/${websiteUrl}`, headers: { 'x-cors-api-key': 'temp_demo' } },
          { name: 'Corsproxy.io', url: `https://corsproxy.io/?${encodeURIComponent(websiteUrl)}` },
          { name: 'CodeTabs', url: `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(websiteUrl)}` }
        ];

        for (const proxy of proxies) {
          try {
            console.log(`📡 Trying ${proxy.name}...`);
            const response = await fetch(proxy.url, {
              headers: {
                'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
                ...(proxy.headers || {})
              },
              signal: AbortSignal.timeout(15000)
            });

            if (response.ok) {
              const data = await response.text();
              if (data && data.length > 100) {
                htmlContent = data;
                fetchMethod = `CORS proxy (${proxy.name})`;
                console.log(`✅ ${proxy.name} succeeded! HTML length: ${htmlContent.length}`);
                break;
              } else {
                console.warn(`⚠️ ${proxy.name} returned minimal content`);
              }
            } else {
              console.warn(`⚠️ ${proxy.name} failed with status: ${response.status}`);
            }
          } catch (proxyError: any) {
            console.warn(`❌ ${proxy.name} error:`, proxyError.message);
            continue;
          }
        }
      }

      // STEP 3: Try web scraping APIs (for JavaScript-rendered sites)
      if (!htmlContent) {
        console.log('🔄 Trying web scraping APIs for JavaScript rendering...');

        // Try ScraperAPI (free tier available)
        try {
          console.log('📡 Trying ScraperAPI...');
          const scraperApiKey = import.meta.env.VITE_SCRAPERAPI_KEY;
          if (scraperApiKey) {
            const scraperUrl = `https://api.scraperapi.com?api_key=${scraperApiKey}&url=${encodeURIComponent(websiteUrl)}&render=true`;
            const response = await fetch(scraperUrl, { signal: AbortSignal.timeout(30000) });
            if (response.ok) {
              htmlContent = await response.text();
              fetchMethod = 'ScraperAPI (JavaScript rendered)';
              console.log(`✅ ScraperAPI succeeded! HTML length: ${htmlContent.length}`);
            }
          }
        } catch (error: any) {
          console.warn('⚠️ ScraperAPI failed:', error.message);
        }
      }

      // STEP 4: Try ScrapingBee (alternative scraping service)
      if (!htmlContent) {
        try {
          console.log('📡 Trying ScrapingBee...');
          const scrapingBeeKey = import.meta.env.VITE_SCRAPINGBEE_API_KEY;
          if (scrapingBeeKey) {
            const scrapingBeeUrl = `https://app.scrapingbee.com/api/v1/?api_key=${scrapingBeeKey}&url=${encodeURIComponent(websiteUrl)}&render_js=true`;
            const response = await fetch(scrapingBeeUrl, { signal: AbortSignal.timeout(30000) });
            if (response.ok) {
              htmlContent = await response.text();
              fetchMethod = 'ScrapingBee (JavaScript rendered)';
              console.log(`✅ ScrapingBee succeeded! HTML length: ${htmlContent.length}`);
            }
          }
        } catch (error: any) {
          console.warn('⚠️ ScrapingBee failed:', error.message);
        }
      }

      // STEP 5: Try Supabase Edge Function (if available)
      if (!htmlContent) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        if (supabaseUrl && supabaseAnonKey) {
          try {
            console.log('📡 Trying Supabase Edge Function...');
            const response = await fetch(`${supabaseUrl}/functions/v1/fetch-rendered-html`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${supabaseAnonKey}`,
              },
              body: JSON.stringify({ url: websiteUrl }),
              signal: AbortSignal.timeout(30000)
            });

            if (response.ok) {
              const result = await response.json();
              if (result.success && result.html) {
                htmlContent = result.html;
                fetchMethod = `Supabase Edge Function (${result.method})`;
                console.log(`✅ Supabase Edge Function succeeded`);
              }
            }
          } catch (edgeError: any) {
            console.warn('⚠️ Supabase Edge Function failed:', edgeError.message);
          }
        }
      }

      // STEP 6: Try RapidAPI web scraping services
      if (!htmlContent) {
        try {
          console.log('📡 Trying RapidAPI web scraper...');
          const rapidApiKey = import.meta.env.VITE_RAPIDAPI_KEY;
          if (rapidApiKey) {
            const response = await fetch('https://web-scraping-api1.p.rapidapi.com/v1/scrape', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-RapidAPI-Key': rapidApiKey,
                'X-RapidAPI-Host': 'web-scraping-api1.p.rapidapi.com'
              },
              body: JSON.stringify({
                url: websiteUrl,
                render_js: true
              }),
              signal: AbortSignal.timeout(30000)
            });

            if (response.ok) {
              const result = await response.json();
              if (result.html) {
                htmlContent = result.html;
                fetchMethod = 'RapidAPI Web Scraper (JavaScript rendered)';
                console.log(`✅ RapidAPI succeeded! HTML length: ${htmlContent.length}`);
              }
            }
          }
        } catch (error: any) {
          console.warn('⚠️ RapidAPI failed:', error.message);
        }
      }

      // STEP 7: Check if we got any content
      if (!htmlContent || htmlContent.length < 100) {
        console.error('❌ Failed to fetch website HTML');
        console.error('   Website:', websiteUrl);
        console.error('   All methods failed:');
        console.error('   ✗ Direct fetch');
        console.error('   ✗ 5 CORS proxy services');
        console.error('   ✗ ScraperAPI');
        console.error('   ✗ ScrapingBee');
        console.error('   ✗ Supabase Edge Function');
        console.error('   ✗ RapidAPI Web Scraper');
        console.error('   This website may be blocking automated access or have strict CORS/bot protection');
        return [];
      }

      console.log(`✅ Successfully fetched website HTML using: ${fetchMethod}`);
      console.log(`📄 HTML content length: ${htmlContent.length} characters`);

      // STEP 8: Extract social links from the HTML
      const extractedProfiles = await this.extractSocialLinksFromHTML(htmlContent);

      // STEP 9: If no social links found and the HTML suggests JavaScript rendering, inform the user
      if (extractedProfiles.length === 0) {
        const isJavaScriptSite = this.detectJavaScriptRenderedSite(htmlContent);
        if (isJavaScriptSite) {
          console.warn('⚠️ This website appears to use JavaScript rendering (React/Next.js/Vue)');
          console.warn('   Social media links may be added dynamically after page load');
          console.warn('   The initial HTML does not contain social media links');
          console.warn('   💡 SOLUTIONS:');
          console.warn('   1. Use ScraperAPI: https://www.scraperapi.com (1000 free requests)');
          console.warn('   2. Use ScrapingBee: https://www.scrapingbee.com (1000 free requests)');
          console.warn('   3. Use RapidAPI: https://rapidapi.com/hub (various scraping APIs)');
          console.warn('   4. Deploy Supabase Edge Function with ScrapingBee integration');
          console.warn('   5. Manually add your social media URLs on the connection page');
        }
      }

      console.log(`✅ Extraction complete: Found ${extractedProfiles.length} social profiles`);

      return extractedProfiles;
    } catch (error) {
      console.error('❌ Website crawling failed with error:', error);
      return [];
    }
  }

  private detectJavaScriptRenderedSite(html: string): boolean {
    // Check for common JavaScript framework indicators
    const jsFrameworkIndicators = [
      /<script[^>]*src=["'][^"']*react[^"']*["']/i,
      /<script[^>]*src=["'][^"']*next[^"']*["']/i,
      /<script[^>]*src=["'][^"']*vue[^"']*["']/i,
      /<script[^>]*src=["'][^"']*angular[^"']*["']/i,
      /BAILOUT_TO_CLIENT_SIDE_RENDERING/i,
      /__NEXT_DATA__/i,
      /__nuxt/i,
      /ng-version=/i,
      /data-reactroot/i,
      /data-react-helmet/i
    ];

    return jsFrameworkIndicators.some(pattern => pattern.test(html));
  }

  private async extractSocialLinksFromHTML(html: string): Promise<SocialMediaProfile[]> {
    const profiles: SocialMediaProfile[] = [];
    const foundUrls = new Set<string>();

    console.log('🔍 Starting social media extraction from HTML...');

    // Helper function to normalize URLs for deduplication AND storage
    const normalizeForDedup = (url: string): string => {
      return this.normalizeUrl(url)
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/^m\./, '')
        .replace(/\/$/, '')
        .split('?')[0]
        .split('#')[0];
    };

    // Helper function to create clean URL for storage (with https://)
    const createCleanUrl = (url: string): string => {
      const normalized = this.normalizeUrl(url)
        .replace(/^www\./, '')
        .replace(/^m\./, '')
        .replace(/\/$/, '')
        .split('?')[0]
        .split('#')[0];
      return normalized;
    };

    // STEP 1: Extract ALL href attributes that might contain social media links
    const allHrefMatches = html.matchAll(/href=["']([^"']+)["']/gi);
    let hrefCount = 0;

    for (const match of allHrefMatches) {
      hrefCount++;
      const url = match[1];

      // Check if this URL contains any social media domain
      const socialDomains = this.socialPlatforms.flatMap(p => p.domains);
      const containsSocialDomain = socialDomains.some(domain => url.toLowerCase().includes(domain));

      if (containsSocialDomain) {
        const normalizedForDedup = normalizeForDedup(url);

        if (!foundUrls.has(normalizedForDedup)) {
          foundUrls.add(normalizedForDedup);
          const platform = this.identifyPlatform(url);

          if (platform) {
            const username = this.extractUsername(url, platform);
            const cleanUrl = createCleanUrl(url);
            console.log(`✅ Found ${platform.name} link: ${url} → cleaned: ${cleanUrl} (username: ${username})`);

            profiles.push({
              platform: platform.name,
              url: cleanUrl,
              username,
              completeness: 70 // Higher confidence for href-extracted links
            });
          }
        }
      }
    }

    console.log(`📊 Scanned ${hrefCount} href attributes, found ${foundUrls.size} social media URLs`);

    // STEP 2: Use platform-specific regex patterns to catch any missed links
    this.socialPlatforms.forEach(platform => {
      platform.patterns.forEach(pattern => {
        const matches = html.matchAll(pattern);
        let platformMatches = 0;

        for (const match of matches) {
          platformMatches++;
          const url = match[0].startsWith('http') ? match[0] : `https://${match[0]}`;
          const normalizedForDedup = normalizeForDedup(url);

          if (!foundUrls.has(normalizedForDedup)) {
            foundUrls.add(normalizedForDedup);
            const username = match[1];
            const cleanUrl = createCleanUrl(url);

            console.log(`✅ Found ${platform.name} via pattern: ${url} → cleaned: ${cleanUrl} (username: ${username})`);

            profiles.push({
              platform: platform.name,
              url: cleanUrl,
              username,
              completeness: 60 // Pattern-matched profiles
            });
          }
        }

        if (platformMatches > 0) {
          console.log(`📊 Platform ${platform.name}: found ${platformMatches} pattern matches`);
        }
      });
    });

    // STEP 3: Filter out non-profile or tracking URLs
    // Note: Facebook share links are allowed through because they redirect to actual profiles when clicked
    const filtered = profiles.filter(p => {
      const isProfile = this.isLikelyProfileUrl(p.platform, p.url, p.username);
      if (!isProfile) {
        console.log(`❌ Filtered out non-profile URL: ${p.url}`);
      }
      return isProfile;
    });

    console.log(`📊 FINAL RESULTS: Found ${profiles.length} total links, ${filtered.length} valid profiles after filtering`);

    if (filtered.length > 0) {
      console.log('✅ Valid social profiles found:');
      filtered.forEach(p => console.log(`   - ${p.platform}: ${p.url}`));
    } else {
      console.warn('⚠️ No valid social media profiles found on website');
    }

    return filtered;
  }

  // REMOVED: _searchByBusinessName - We don't guess profiles based on business names
  // REMOVED: _searchByDomain - We don't guess profiles based on domain names
  // We ONLY show social media links that are actually found on the website

  // REMOVED: checkProfileExists - We don't create fake/simulated profiles
  // We ONLY show real social media links found on the actual website

  // REMOVED: All heuristic/guessing helper methods
  // We ONLY use actual links found on the website

  // REMOVED: _fallbackSocialDetection - We don't generate fake profiles
  // REMOVED: generateUsernameVariations - We don't guess usernames
  // We ONLY show social media links that are actually on the website HTML

  private extractDomain(url: string): string {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    }
  }

  private normalizeUrl(url: string): string {
    if (!url.startsWith('http')) {
      return `https://${url}`;
    }
    return url;
  }

  // Heuristic filter to exclude non-profile and tracking URLs
  private isLikelyProfileUrl(platform: string, url: string, username?: string): boolean {
    const u = url.toLowerCase();

    // Platform-specific checks FIRST (before general banned list)
    if (platform === 'facebook') {
      // Allow profile.php URLs (these are valid Facebook profile URLs with numeric IDs)
      if (u.includes('profile.php?id=')) return true;
      // Block Facebook-specific non-profile URLs
      if (u.includes('facebook.com/') && (u.endsWith('/tr') || u.includes('/events/'))) return false;
    }

    // General banned patterns (but already checked platform-specific exceptions above)
    const banned = [
      '/tr', '/watch', '/embed', '/intent', '/sharer.php', 'sharer.php',
      '/oauth', '/dialog', '/plugins/', '/shorts', '/hashtag', '/home', '/i/', '/privacy', '/help'
    ];
    if (banned.some(b => u.includes(b))) return false;

    // Allow Facebook share links since they're valid (they redirect to actual profiles when clicked)
    if (platform === 'facebook' && u.includes('/share/')) {
      return true;
    }
    if (platform === 'youtube') {
      if (u.includes('youtube.com/watch') || u.includes('youtube.com/embed')) return false;
    }
    if (platform === 'instagram') {
      if (u.includes('/p/') || u.includes('/reel') || u.includes('/explore')) return false;
    }
    if (platform === 'twitter' || platform === 'x') {
      if (u.includes('/intent') || u.includes('/hashtag') || u.includes('/home') || u.includes('/i/')) return false;
    }
    if (platform === 'linkedin') {
      if (u.includes('/feed') || u.includes('/sharing') || u.includes('shareArticle')) return false;
    }

    const badUsernames = ['watch', 'tr', 'share'];
    if (username && badUsernames.includes(username.toLowerCase())) return false;

    try {
      const parsed = new URL(u.startsWith('http') ? u : `https://${u}`);
      const segments = parsed.pathname.split('/').filter(Boolean);
      if (segments.length === 0) return false;
    } catch {}

    return true;
  }

  private removeDuplicates(profiles: SocialMediaProfile[]): SocialMediaProfile[] {
    const seen = new Map<string, SocialMediaProfile>();

    profiles.forEach(profile => {
      // Normalize URL for comparison (remove trailing slashes, query params, www, protocol differences)
      const normalizedUrl = profile.url
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/^m\./, '') // Remove mobile prefix
        .replace(/\/$/, '')
        .split('?')[0]
        .split('#')[0];

      // Use normalized URL as key (not platform + url) to catch cross-platform duplicates
      const key = normalizedUrl;

      // If this exact URL already exists, keep only one
      if (seen.has(key)) {
        const existing = seen.get(key)!;
        // Keep the one with more data (higher completeness or with follower count)
        const existingScore = (existing.completeness || 0) + (existing.followers ? 50 : 0);
        const newScore = (profile.completeness || 0) + (profile.followers ? 50 : 0);

        if (newScore > existingScore) {
          seen.set(key, profile);
        }
      } else {
        seen.set(key, profile);
      }
    });

    return Array.from(seen.values());
  }

  private async enhanceProfiles(profiles: SocialMediaProfile[]): Promise<SocialMediaProfile[]> {
    // Return profiles without attempting to fetch follower counts
    // This avoids CORS errors and unnecessary API calls
    return profiles.map((profile) => ({
      ...profile,
      followers: undefined,
      engagement: undefined,
      verified: false,
      completeness: 70
    }));
  }

  private calculateOverallScore(profiles: SocialMediaProfile[], websiteQuality?: WebsiteQuality): number {
    if (profiles.length === 0 && (!websiteQuality || websiteQuality.isBlankWebsite)) {
      return 0; // No social media and blank website = 0 score
    }

    let totalScore = 0;
    const platformScores = this.socialPlatforms.reduce((acc, platform) => {
      acc[platform.name] = platform.baseScore;
      return acc;
    }, {} as Record<string, number>);

    // Website quality penalty for blank websites
    let websiteQualityPenalty = 0;
    if (websiteQuality) {
      if (websiteQuality.isBlankWebsite) {
        websiteQualityPenalty = 50; // Major penalty for blank websites
      } else if (websiteQuality.score < 40) {
        websiteQualityPenalty = 20; // Moderate penalty for poor quality websites
      } else if (websiteQuality.score < 60) {
        websiteQualityPenalty = 10; // Small penalty for low quality websites
      }
    }

    profiles.forEach(profile => {
      let profileScore = platformScores[profile.platform] || 10;

      // Completeness bonus
      if (profile.completeness) {
        profileScore += (profile.completeness / 100) * 15;
      }

      // Follower count bonus
      if (profile.followers) {
        if (profile.followers > 10000) profileScore += 15;
        else if (profile.followers > 1000) profileScore += 10;
        else if (profile.followers > 100) profileScore += 5;
      }

      // Verified bonus
      if (profile.verified) {
        profileScore += 10;
      }

      // Engagement bonus
      if (profile.engagement && profile.engagement > 2) {
        profileScore += 5;
      }

      totalScore += profileScore;
    });

    // Penalty for having no profiles on major platforms
    const majorPlatforms = ['facebook', 'instagram', 'twitter', 'linkedin'];
    const foundMajorPlatforms = profiles.filter(p => majorPlatforms.includes(p.platform));

    if (foundMajorPlatforms.length === 0) {
      totalScore = Math.max(0, totalScore - 20);
    }

    // Apply website quality penalty
    totalScore = Math.max(0, totalScore - websiteQualityPenalty);

    return Math.min(Math.round(totalScore), 100);
  }

  // New helper methods for enhanced social media detection
  private identifyPlatform(url: string) {
    const lowerUrl = url.toLowerCase();
    return this.socialPlatforms.find(platform =>
      platform.domains.some(domain => lowerUrl.includes(domain))
    );
  }

  private extractUsername(url: string, platform: any): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;

      // Extract username based on platform-specific patterns
      if (platform.name === 'facebook') {
        const match = pathname.match(/\/([^\/\?]+)/);
        return match ? match[1] : '';
      } else if (platform.name === 'instagram') {
        const match = pathname.match(/\/([^\/\?]+)/);
        return match ? match[1] : '';
      } else if (platform.name === 'twitter') {
        const match = pathname.match(/\/([^\/\?]+)/);
        return match ? match[1] : '';
      } else if (platform.name === 'linkedin') {
        const match = pathname.match(/\/(company|in)\/([^\/\?]+)/);
        return match ? match[2] : '';
      } else if (platform.name === 'youtube') {
        const match = pathname.match(/\/(c|channel|user|@)\/([^\/\?]+)/) || pathname.match(/\/([^\/\?]+)/);
        return match ? match[2] || match[1] : '';
      } else if (platform.name === 'tiktok') {
        const match = pathname.match(/\/@([^\/\?]+)/);
        return match ? match[1] : '';
      } else if (platform.name === 'threads') {
        const match = pathname.match(/\/@([^\/\?]+)/);
        return match ? match[1] : '';
      }

      return '';
    } catch {
      return '';
    }
  }

  private async enhanceFoundSocialProfiles(profiles: SocialMediaProfile[]): Promise<SocialMediaProfile[]> {
    // Return profiles without attempting to fetch follower counts
    // This avoids CORS errors and unnecessary API calls
    return profiles.map((profile) => ({
      ...profile,
      followers: undefined,
      engagement: undefined,
      verified: false,
      completeness: 70
    }));
  }




}

export const socialMediaDetector = new SocialMediaDetector();